import {
  getSimplifiedLevels,
  getTraditionalLevels,
} from "@-/dev.improve.haniverse.assets.graphemes.levels";
import { getWordLevels } from "@-/dev.improve.haniverse.assets.words.levels";
import {
  formatPronunciationLevelLabel,
  formatWordLevelLabel,
  withoutHardcodedPronunciationLevelWords,
} from "@-/dev.improve.haniverse.lib.level";
import type { Level } from "@-/dev.improve.haniverse.lib.level";
import {
  type CustomCard,
  type CustomLevel,
  type CustomLevelPack,
} from "@-/dev.improve.haniverse.lib.level.custom";

export interface CustomCopySource {
  id: string;
  levels: readonly CustomLevel[];
  name: string;
}

export function getCustomCopySources(packs: readonly CustomLevelPack[]): CustomCopySource[] {
  return [
    ...(["simplified", "traditional"] as const).map((script) => {
      const characters: readonly Level[] =
        script === "simplified" ? getSimplifiedLevels() : getTraditionalLevels();
      const words =
        script === "simplified"
          ? withoutHardcodedPronunciationLevelWords(getWordLevels(script))
          : getWordLevels(script);
      const card = (morpheme: string, part: CustomCard["part"], id: string): CustomCard => ({
        id,
        kind: "card",
        morpheme,
        part,
      });
      let pronunciationIndex = 0;
      const levels: CustomLevel[] = [];
      for (const level of characters) {
        levels.push({
          description: level.description,
          id: `${script}-${level.index}`,
          items: level.characters.map((character, index) =>
            card(
              character,
              level.meaningOnly ? "meaning" : "meaning-and-pronunciation",
              `${script}-${level.index}-${index}`,
            ),
          ),
          name: `Level ${level.index + 1}${level.name ? ` · ${level.name}` : ""}`,
        });
        if (level.pronunciationCharacters?.length) {
          levels.push({
            description: "Pronunciation",
            id: `${script}-${level.index}-pronunciation`,
            items: [
              ...level.pronunciationCharacters.map((character, index) =>
                card(character, "pronunciation", `${script}-${level.index}-p-${index}`),
              ),
              ...(level.pronunciationWords ?? []).map((word, index) =>
                card(word, "word", `${script}-${level.index}-p-word-${index}`),
              ),
            ],
            name: `Level ${formatPronunciationLevelLabel(level, pronunciationIndex++)}`,
          });
        }
        for (const wordLevel of words.filter(
          (entry) => entry.characterLevelIndex === level.index,
        )) {
          levels.push({
            description: "Vocabulary",
            id: `${script}-word-${wordLevel.index}`,
            items: wordLevel.words.map((word, index) =>
              card(word, "word", `${script}-w-${wordLevel.index}-${index}`),
            ),
            name: `Level ${formatWordLevelLabel(wordLevel)}`,
          });
        }
      }
      return { id: script, levels, name: script === "simplified" ? "Simplified" : "Traditional" };
    }),
    ...packs.map((pack) => ({ id: pack.id, levels: pack.levels, name: pack.name })),
  ];
}
